<template>
  <div class="base-input" :class="computedClass">
    <div class="base-input__container" @click="emit('click')">
      <slot v-if="$slots['before-input']" name="before-input">
        <BaseIcon
          v-if="props.beforeIcon"
          :name="props.beforeIcon"
          color="var(--color-foreground-primary)"
          size="20px"
        />
      </slot>
      <div class="base-input__wrapper">
        <div
          v-if="$slots['prepend-icon'] || props.prependIcon"
          class="base-input__icon"
        >
          <slot name="prepend-icon">
            <BaseIcon
              v-if="props.prependIcon"
              :name="props.prependIcon"
              color="var(--color-foreground-primary)"
              size="20px"
            />
          </slot>
        </div>
        <component
          :is="props.typography ? 'textarea' : 'input'"
          ref="inputRef"
          v-maska:unmaskedValue.unmasked="options"
          :value="props.modelValue"
          :tabindex="props.tabindex"
          :name="props.name"
          :type="type"
          :placeholder="props.placeholder"
          :readonly="props.readonly"
          :maxlength="props.maxlength"
          :inputmode="props.inputmode"
          class="base-input__control"
          :disabled="props.disabled"
          @input="handleChangeValue"
          @focus="emit('focus')"
          @blur="emit('blur')"
          @keydown.delete="handleKeyDown"
          @keydown.enter="emit('keydownEnter')"
        />
        <div
          v-if="
            $slots['append-icon']
              || props.appendIcon
              || props.readonly
          "
          class="base-input__icon"
        >
          <slot name="append-icon">
            <BaseTypography
              v-if="props.successMessage"
              type="text-mini"
              color="var(--color-foreground-success)"
              nowrap
              style="margin-right: 8px;"
            >
              {{ props.successMessage }}
            </BaseTypography>
            <BaseIcon
              v-if="props.appendIcon"
              :name="props.appendIcon"
              :color="props.appendIconColor"
              size="20px"
              clickable
              @click="emit('clickAppendIcon', props.name)"
            />
            <BaseIcon
              v-else-if="props.readonly && props.withReadonlyIcon"
              name="vuesax:edit"
              :color="props.appendIconColor"
              size="20px"
              clickable
              @click="emit('clickAppendIcon', props.name)"
            />
          </slot>
        </div>
      </div>
      <slot v-if="$slots['after-input'] || hasClear" name="after-input" v-bind="{ hasClear }">
        <BaseButtonIcon
          v-if="hasClear"
          icon-name="vuesax:close-circle-linear"
          icon-color="var(--color-foreground-caption)"
          color="var(--color-surface-placeholder)"
          size="md-alt-2"
          :variant="props.variant ?? 'secondary'"
          @click.stop="handleClear"
        />
        <BaseIcon
          v-else-if="props.afterIcon"
          :name="props.afterIcon"
          color="var(--color-foreground-primary)"
          size="20px"
        />
      </slot>
    </div>
    <BaseTypography
      v-if="props.errorMessage && props.withErrorLabel"
      class="base-input__error-message"
      type="text-mini-alt"
      color="var(--color-foreground-danger)"
    >
      {{ props.errorMessage }}
    </BaseTypography>
  </div>
</template>

<script setup lang="ts">
import type { MaskInputOptions } from 'maska'
import type { RefFor } from '../../lib/type'
import { useAnimationShake } from '../../lib'
import BaseButtonIcon from '../button-icon/index.vue'
import BaseIcon from '../icon/index.vue'
import BaseTypography from '../typography/index.vue'

export interface IProps {
  modelValue?: string
  name?: string
  placeholder?: string
  disabled?: boolean
  tabindex?: number
  size?: 'md' | 'md-alt' | 'lg' | 'xl'
  type?:
    | 'number'
    | 'time'
    | 'text'
    | 'password'
    | 'email'
    | 'search'
    | 'tel'
    | 'file'
    | 'url'
    | 'date'
    | 'datetime-local'
  mask?: string
  readonly?: boolean
  variant?: 'light' | 'secondary'
  withShadow?: boolean
  withFull?: boolean
  withClear?: boolean
  alwaysShowClear?: boolean
  prependIcon?: string
  appendIcon?: string
  appendIconColor?: string
  beforeIcon?: string
  afterIcon?: string
  withReadonlyIcon?: boolean
  inputmode?:
    | 'text'
    | 'email'
    | 'search'
    | 'tel'
    | 'url'
    | 'none'
    | 'numeric'
    | 'decimal'
    | undefined
  typography?:
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6'
    | 'title'
    | 'subtitle'
    | 'text'
    | 'text-secondary'
    | 'text-mini'
    | 'text-mini-alt'
    | 'caption'
    | 'label'
  errorMessage?: string
  successMessage?: string
  withErrorLabel?: boolean
  maxlength?: number
}

export interface IEmit {
  (event: 'click'): void
  (event: 'focus'): void
  (event: 'blur'): void
  (event: 'clickAppendIcon', name?: string): void
  (event: 'update:modelValue', value: string | number | null): void
  (event: 'getUnmaskedValue', value: string | number | null): void
  (event: 'cleared', name?: string): void
  (event: 'keydownEnter'): void
}

export interface Expose {
  unmaskedValue: string
  focus: () => void
}

const props = withDefaults(defineProps<IProps>(), {
  modelValue: '',
  size: 'md',
  withReadonlyIcon: true,
  disabled: false,
  appendIconColor: 'var(--color-foreground-primary)',
})
const emit = defineEmits<IEmit>()

const options = computed<MaskInputOptions>(() => ({
  mask: props.mask,
  eager: true,
  tokens: { 0: { pattern: /@/, optional: true } },
}))

const inputRef = ref<HTMLInputElement>()
const unmaskedValue = ref<string>('')
const lastEmittedValue = ref<string | number | null>(null)

const { shakeClass } = useAnimationShake(() => props.errorMessage)

const computedClass = computed(() => ({
  [`base-input__size-${props.size}`]: true,
  [shakeClass.value]: true,
  'base-input--with-full': props.withFull,
  [`base-input--variant-${props.variant}`]: props.variant,
  'base-input--with-shadow': props.withShadow,
  'base-input--error': props.errorMessage,
  'base-input--success': props.successMessage,
  'base-input--typography': props.typography,
  [`base-input--typography-${props.typography}`]: props.typography,
}))
const hasClear = computed(() => props.withClear && (props.modelValue || props.alwaysShowClear))

function adjustHeight() {
  if (!props.typography) {
    return
  }

  if (inputRef.value instanceof HTMLTextAreaElement) {
    inputRef.value.style.height = '1rem'
    inputRef.value.style.height = `${inputRef.value.scrollHeight}px`
  }
}

if (props.typography) {
  onMounted(adjustHeight)
}

function handleChangeValue(event: Event) {
  const value = (event.target as HTMLInputElement).value

  if (props.typography) {
    adjustHeight()
  }

  nextTick(() => {
    emit('getUnmaskedValue', unmaskedValue.value)

    const valueToEmit = props.type === 'number' ? +value : value

    if (lastEmittedValue.value === valueToEmit) {
      return
    }

    lastEmittedValue.value = valueToEmit

    if (props.type === 'number') {
      emit('update:modelValue', +value)
    }
    else {
      emit('update:modelValue', value)
    }
  })
}

function handleClear() {
  lastEmittedValue.value = ''
  emit('getUnmaskedValue', '')
  emit('update:modelValue', '')
  emit('cleared', props.name)
}

function handleKeyDown() {
  if (!props.modelValue) {
    emit('cleared', props.name)
  }
}

function focus() {
  setTimeout(() => inputRef.value?.focus({ preventScroll: true }), 200)
}

defineExpose<RefFor<Expose, 'unmaskedValue'>>({
  unmaskedValue,
  focus,
})
</script>

<style scoped lang="scss">
.base-input {
  display: flex;
  flex-direction: column;

  &__container {
    display: flex;
    gap: rem(4px);
    border-radius: rem(12px);
    background-color: var(--color-surface-placeholder);
    border: 2px solid var(--color-surface-placeholder);
  }

  &__wrapper {
    width: 100%;
    display: inline-flex;
    align-items: center;
    position: relative;
  }

  &__control {
    @include drop-button-styles;

    width: 100%;
    padding: rem(12px);
    border-radius: rem(12px);
    will-change: scale;
    height: calc(var(--base-input-height, 38px) - 4px);
    color: var(--color-foreground-regular);

    &::placeholder {
      color: var(--color-foreground-caption);
    }

    &:disabled {
      color: var(--color-foreground-caption);
    }
  }

  textarea.base-input__control {
    resize: none;
  }

  &__error-message {
    height: 10px;
    margin-bottom: -10px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__icon + &__control {
    padding-left: rem(8px);
  }

  &__icon {
    height: 100%;
    display: flex;
    align-items: center;

    &:first-child {
      padding-left: rem(9px);
    }

    &:last-child {
      padding-right: rem(9px);
    }
  }

  &__size-md {
    .base-input__container {
      font-size: rem(14px);
      line-height: rem(14px);
      font-weight: 400;
    }

    .base-input__control {
      height: calc(var(--base-input-height, 38px) - 4px);
    }

    &-alt {
      .base-input__control {
        height: calc(var(--base-input-height, 40px) - 4px);
      }
    }
  }

  &__size-lg {
    .base-input__container {
      font-size: rem(16px);
      line-height: rem(19.5px);
      font-weight: 500;
    }

    .base-input__control {
      height: calc(var(--base-input-height, 40px) - 4px);
    }
  }

  &__size-xl {
    .base-input__container {
      font-size: rem(20px);
      line-height: rem(20px);
      font-weight: 400;
    }

    .base-input__control {
      height: calc(var(--base-input-height, 46px) - 4px);
    }
  }

  &--variant {
    &-light .base-input__container {
      background-color: var(--color-surface-bg);
      border: 2px solid var(--color-surface-bg);
    }
  }

  &--typography {
    .base-input__container {
      border-radius: 0;
      background-color: transparent;
      border: 0;
    }

    .base-input__control {
      border-radius: 0;
      height: auto;
      padding: 0;
    }

    &-h1 .base-input__control {
      @include typo-h1;
    }

    &-h2 .base-input__control {
      @include typo-h2;
    }

    &-h3 .base-input__control {
      @include typo-h3;
    }

    &-h4 .base-input__control {
      @include typo-h4;
    }

    &-h5 .base-input__control {
      @include typo-h5;

      font-weight: 700;
    }

    &-h6 .base-input__control {
      @include typo-h6;
    }

    &-title .base-input__control {
      @include typo-title;

      line-height: rem(24px);
    }

    &-subtitle .base-input__control {
      @include typo-subtitle;
    }

    &-text .base-input__control {
      @include typo-text;
    }

    &-text-secondary .base-input__control {
      @include typo-text-secondary;
    }

    &-text-mini .base-input__control {
      @include typo-text-mini;
    }

    &-text-mini-alt .base-input__control {
      @include typo-text-mini-alt;
    }

    &-caption .base-input__control {
      @include typo-caption;
    }

    &-label .base-input__control {
      @include typo-label;
    }
  }

  &--with-full {
    width: 100%;
  }

  &--with-shadow &__container {
    box-shadow: 0 0 12px 0 #3333;
  }

  &--success &__container {
    border: 2px solid var(--color-surface-success);

    &.base-input--typography {
      border-bottom: 2px solid var(--color-surface-success);
    }
  }

  &--error &__container {
    border: 2px solid var(--color-surface-danger);

    &.base-input--typography {
      border-bottom: 2px solid var(--color-surface-danger);
    }
  }
}
</style>
